import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Table, Tag, Space, Button, Select, Typography, Alert, Tooltip, Popconfirm, message } from 'antd';
import { PlusOutlined, FileTextOutlined, ReloadOutlined, ExperimentOutlined, BarChartOutlined } from '@ant-design/icons';
import { surveyService } from '../../services/survey.service';
import { surveyPreviewAnalysisService } from '../../services/surveyPreviewAnalysis.service';
import { surveyAnalyticsService } from '../../services/surveyAnalytics.service';
import {
  Survey,
  SurveyStatus,
  SurveyAnalysisStatus,
  ComplementaryAnalysisStatus,
} from '../../types/survey.types';
import { SurveyImportDialog } from '../../components/admin/surveys/SurveyImportDialog';

// Phase 4 - US2: Surveys List (T081)

const analysisStatusMeta: Record<SurveyAnalysisStatus, { label: string; color: string }> = {
  [SurveyAnalysisStatus.PENDING]: { label: 'Pendente', color: 'default' },
  [SurveyAnalysisStatus.IN_PROGRESS]: { label: 'Processando', color: 'processing' },
  [SurveyAnalysisStatus.COMPLETED_UNPUBLISHED]: { label: 'Aguardando liberação', color: 'gold' },
  [SurveyAnalysisStatus.PUBLISHED]: { label: 'Liberada', color: 'green' },
};

const versionFreshnessMeta: Record<'CURRENT' | 'OUTDATED' | 'LEGACY', { label: string; color: string; tooltip: string }> = {
  CURRENT: {
    label: 'Versão mais recente',
    color: 'green',
    tooltip: 'A análise foi gerada com a versão de cálculo mais recente.',
  },
  OUTDATED: {
    label: 'Versão desatualizada',
    color: 'gold',
    tooltip: 'A análise foi gerada com uma versão de cálculo anterior. Reprocesse para atualizar.',
  },
  LEGACY: {
    label: 'Versão legada',
    color: 'default',
    tooltip: 'Esta análise ainda não passou pelo pipeline de versões atual. Reprocesse para migrar.',
  },
};

const complementaryStatusMeta: Record<ComplementaryAnalysisStatus, { label: string; color: string }> = {
  [ComplementaryAnalysisStatus.DRAFT]: { label: 'Rascunho', color: 'default' },
  [ComplementaryAnalysisStatus.PENDING_SCAN]: { label: 'Varredura pendente', color: 'processing' },
  [ComplementaryAnalysisStatus.READY_TO_PUBLISH]: { label: 'Aguardando liberação', color: 'gold' },
  [ComplementaryAnalysisStatus.PUBLISHED]: { label: 'Liberada', color: 'green' },
};

export const SurveysPage: React.FC = () => {
  const navigate = useNavigate();
  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<SurveyStatus | undefined>();
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [reprocessingId, setReprocessingId] = useState<string | null>(null);
  const [previewTriggeringId, setPreviewTriggeringId] = useState<string | null>(null);

  useEffect(() => {
    loadSurveys();
  }, [statusFilter, page]);

  const loadSurveys = async () => {
    try {
      setLoading(true);
      const result = await surveyService.listSurveys({
        status: statusFilter,
        page,
        limit: 20,
      });
      setSurveys(result.data);
      setTotal(result.total);
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar pesquisas');
    } finally {
      setLoading(false);
    }
  };

  const REPROCESS_POLL_INTERVAL_MS = 4000;
  const REPROCESS_POLL_TIMEOUT_MS = 5 * 60 * 1000;

  const handleReprocess = async (survey: Survey) => {
    try {
      setReprocessingId(survey.id);
      const accepted = await surveyService.reprocessAnalysisNow(survey.id);

      const deadline = Date.now() + REPROCESS_POLL_TIMEOUT_MS;
      let finished: Awaited<ReturnType<typeof surveyAnalyticsService.getVersion>> | null = null;
      while (Date.now() < deadline) {
        await new Promise((resolve) => setTimeout(resolve, REPROCESS_POLL_INTERVAL_MS));
        const version = await surveyAnalyticsService.getVersion(survey.id, accepted.versionId);
        if (version.status === 'COMPLETED' || version.status === 'FAILED') {
          finished = version;
          break;
        }
      }

      if (!finished) {
        message.warning('O processamento continua em andamento em segundo plano. Atualize a lista em instantes.');
      } else if (finished.status === 'COMPLETED') {
        message.success(
          `Análise reprocessada${finished.participantCount != null ? ` com ${finished.participantCount} resposta(s) consideradas` : ''}.`
        );
      } else {
        message.error(finished.failure?.message || 'Falha ao reprocessar a análise.');
      }
      await loadSurveys();
    } catch (err: any) {
      message.error(err?.response?.data?.message || err.message || 'Erro ao reprocessar a análise');
    } finally {
      setReprocessingId(null);
    }
  };

  const handleTriggerPreview = async (survey: Survey) => {
    try {
      setPreviewTriggeringId(survey.id);
      await surveyPreviewAnalysisService.triggerPreviewAnalysis(survey.id);
      message.success('Análise prévia executada. Abra a tela de análises prévias para visualizar.');
      await loadSurveys();
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 409) {
        message.warning('Já existe uma análise prévia em andamento para esta pesquisa.');
      } else {
        message.error(
          err?.response?.data?.message || err.message || 'Erro ao executar a análise prévia.'
        );
      }
    } finally {
      setPreviewTriggeringId(null);
    }
  };

  const statusMeta: Record<SurveyStatus, { label: string; color: string }> = {
    DRAFT: { label: 'Rascunho', color: 'default' },
    SCHEDULED: { label: 'Agendada', color: 'processing' },
    ACTIVE: { label: 'Ativa', color: 'green' },
    PAUSED: { label: 'Pausada', color: 'orange' },
    COMPLETED: { label: 'Concluída', color: 'blue' },
    CANCELED: { label: 'Cancelada', color: 'red' },
  };

  const columns = [
    {
      title: 'Nome',
      dataIndex: 'name',
      key: 'name',
      render: (_: string, record: Survey) => (
        <Link to={`/admin/surveys/${record.id}`}>{record.name}</Link>
      ),
    },
    {
      title: 'Contratante',
      dataIndex: ['contractor', 'tradeName'],
      key: 'contractor',
      render: (_: any, record: Survey) => record.contractor?.tradeName || 'N/A',
    },
    {
      title: 'Período',
      key: 'period',
      render: (_: any, record: Survey) => {
        const start = new Date(record.startDate).toLocaleDateString('pt-BR');
        const end = new Date(record.endDate).toLocaleDateString('pt-BR');
        return `${start} - ${end}`;
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: SurveyStatus) => {
        const meta = statusMeta[status];
        return <Tag color={meta?.color}>{meta?.label || status}</Tag>;
      },
    },
    {
      title: 'Questões',
      key: 'questions',
      render: (_: any, record: Survey) => record.questions?.length ?? 0,
    },
    {
      title: 'Respostas',
      key: 'responses',
      render: (_: any, record: Survey) => {
        const totalResponses = record._count?.responses ?? 0;
        const analyzed = record.analysis?.analyzedResponseCount;
        const mismatch =
          analyzed !== null && analyzed !== undefined && analyzed !== totalResponses;
        return (
          <Space size={4}>
            <span>{totalResponses}</span>
            {analyzed !== null && analyzed !== undefined && (
              <Tooltip title="Respostas consideradas na análise atual">
                <Tag color={mismatch ? 'warning' : 'default'} style={{ marginInlineEnd: 0 }}>
                  análise: {analyzed}
                </Tag>
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Status da análise',
      key: 'analysisStatus',
      render: (_: any, record: Survey) => {
        if (!record.analysis) {
          return <Tag color="default">Sem análise</Tag>;
        }
        const meta = analysisStatusMeta[record.analysis.status];
        const freshness = record.analysis.versionFreshness
          ? versionFreshnessMeta[record.analysis.versionFreshness]
          : null;
        return (
          <Space direction="vertical" size={4}>
            <Tag color={meta?.color}>{meta?.label || record.analysis.status}</Tag>
            {freshness && (
              <Tooltip title={freshness.tooltip}>
                <Tag color={freshness.color}>{freshness.label}</Tag>
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Análise complementar',
      key: 'complementaryStatus',
      render: (_: any, record: Survey) => {
        if (!record.complementaryAnalysis) {
          return <Tag color="default">Não iniciada</Tag>;
        }
        const meta = complementaryStatusMeta[record.complementaryAnalysis.status];
        return (
          <Tag color={meta?.color}>{meta?.label || record.complementaryAnalysis.status}</Tag>
        );
      },
    },
    {
      title: 'Análises prévias',
      key: 'previewAnalyses',
      render: (_: any, record: Survey) => {
        const count = record.previewAnalysisCount ?? 0;
        return (
          <Link to={`/admin/surveys/${record.id}/analysis/preview`}>
            {count > 0 ? `${count} análise(s)` : 'Ver / executar'}
          </Link>
        );
      },
    },
    {
      title: 'Ações',
      key: 'actions',
      render: (_: any, record: Survey) => {
        const canRunPreview =
          record.status === SurveyStatus.ACTIVE && (record._count?.responses ?? 0) > 0;
        return (
        <Space>
          <Link to={`/admin/surveys/${record.id}`}>Editar</Link>
          <Button
            type="link"
            icon={<FileTextOutlined />}
            style={{ padding: 0 }}
            onClick={() => navigate(`/admin/surveys/${record.id}/questionnaire-report`)}
          >
            Relatório do Questionário
          </Button>
          {record.status === SurveyStatus.COMPLETED && (
            <Button
              type="link"
              icon={<BarChartOutlined />}
              style={{ padding: 0 }}
              onClick={() => navigate(`/admin/surveys/${record.id}/analysis`)}
            >
              Ver Análise
            </Button>
          )}
          {(record.analysis || record.status === SurveyStatus.COMPLETED) && (
            <Popconfirm
              title="Reprocessar análise"
              description="A análise será recalculada com todas as respostas atuais. Uma análise já liberada permanece liberada com o resultado atualizado. O processamento roda em segundo plano e pode levar alguns minutos."
              okText="Reprocessar"
              cancelText="Cancelar"
              onConfirm={() => handleReprocess(record)}
            >
              <Button
                type="link"
                icon={<ReloadOutlined />}
                style={{ padding: 0 }}
                loading={reprocessingId === record.id}
                disabled={record.analysis?.status === SurveyAnalysisStatus.IN_PROGRESS}
              >
                Reprocessar análise
              </Button>
            </Popconfirm>
          )}
          {canRunPreview && (
            <Popconfirm
              title="Executar análise prévia"
              description="Gera uma leitura antecipada dos resultados parciais. Visível apenas para administradores; nunca é publicada."
              okText="Executar"
              cancelText="Cancelar"
              onConfirm={() => handleTriggerPreview(record)}
            >
              <Button
                type="link"
                icon={<ExperimentOutlined />}
                style={{ padding: 0 }}
                loading={previewTriggeringId === record.id}
              >
                Executar análise prévia
              </Button>
            </Popconfirm>
          )}
        </Space>
        );
      },
    },
  ];

  return (
    <div className="surveys-page">
      <Space style={{ width: '100%', marginBottom: 16, display: 'flex', justifyContent: 'space-between' }} align="center">
        <Typography.Title level={2} style={{ margin: 0 }}>
          Pesquisas
        </Typography.Title>
        <Space>
          <SurveyImportDialog onImported={loadSurveys} />
          <Link to="/admin/tags">
            <Button>Etiquetas</Button>
          </Link>
          <Link to="/admin/surveys/new">
            <Button type="primary" icon={<PlusOutlined />}>Nova Pesquisa</Button>
          </Link>
        </Space>
      </Space>

      <Space style={{ marginBottom: 16 }}>
        <Select
          value={statusFilter || 'all'}
          onChange={(value) =>
            setStatusFilter(value === 'all' ? undefined : (value as SurveyStatus))
          }
          style={{ width: 220 }}
        >
          <Select.Option value="all">Todos os Status</Select.Option>
          <Select.Option value={SurveyStatus.DRAFT}>Rascunho</Select.Option>
          <Select.Option value={SurveyStatus.SCHEDULED}>Agendada</Select.Option>
          <Select.Option value={SurveyStatus.ACTIVE}>Ativa</Select.Option>
          <Select.Option value={SurveyStatus.PAUSED}>Pausada</Select.Option>
          <Select.Option value={SurveyStatus.COMPLETED}>Concluída</Select.Option>
          <Select.Option value={SurveyStatus.CANCELED}>Cancelada</Select.Option>
        </Select>
      </Space>

      {error && <Alert type="error" message={error} style={{ marginBottom: 16 }} />}

      <Table
        rowKey="id"
        loading={loading}
        dataSource={surveys}
        columns={columns}
        scroll={{ x: 'max-content' }}
        pagination={{
          current: page,
          pageSize: 20,
          total,
          onChange: (p) => setPage(p),
        }}
      />
    </div>
  );
};
